import { DateTime } from '@ksyos/date-time';
import { Link, TableBody, TableCell, TableCellSelectRow, TableRow } from '@ksyos/design-system';
import { type FundingErrorInformation, ManagerAuthorization } from '@common/enums';
import { Dropdown } from '@frontend/components/elements/common';
import { DisplayAmountWithVatRate } from '@frontend/components/elements/displayAmountWithVatRate';
import { DisplayShortWithTooltip } from '@frontend/components/elements/DisplayShortWithTooltip';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import type { Financing } from '../financing';
import { canSubTrackBeExported } from '../../canSubTrackBeExported';
import { getFundingErrorInformationOptions } from '../../getFundingErrorInformationOptions';
import { getIsUsedForReprocessing } from './financingHelperFunctions';

type FinancingRow = Financing & {
    amount?: number;
    codeActiviteit?: string | null;
    group?: string | null;
    institution?: string | null;
    isCounterBooked?: boolean;
    relationNumber?: string | null;
    runNumber?: number | null;
    vatRate?: string;
};

type FinancingTableOldBodyProps = {
    items: Financing[];
    onChangeFundingErrorInformation: (value: FundingErrorInformation | null, financingItemIndex: number) => void;
    onSelectFinancingItem: (id: string, value: boolean) => void;
    reprocessedActivities: string[];
};

export function FinancingTableOldBody(props: FinancingTableOldBodyProps) {
    const { items, onSelectFinancingItem, onChangeFundingErrorInformation, reprocessedActivities } = props;
    const language = getSelectedLanguage();

    return (
        <TableBody>
            {items.map(mapFinanceRow).map((item, index) => {
                const isUsedForReprocessing = getIsUsedForReprocessing(items, item);

                const notFundedReasonText = getNotFundedReasonText(item);

                return (
                    <TableRow key={index}>
                        {isUsedForReprocessing ? (
                            <TableCellSelectRow
                                isSelected={item.checked}
                                onSelect={async (event) =>
                                    onSelectFinancingItem(item.id, (event.target as HTMLInputElement).checked)
                                }
                            />
                        ) : (
                            <TableCell />
                        )}
                        <TableCell>
                            {hasOneAuthorization(ManagerAuthorization.adminProcessRead) ? (
                                <Link
                                    to={'/processes/' + item.id}
                                    variant="link"
                                >
                                    {item.processId}
                                </Link>
                            ) : (
                                item.processId
                            )}
                        </TableCell>
                        <TableCell>
                            <NullableFormattedDate value={item.created_at} />
                        </TableCell>
                        <TableCell>{item.txcsProcesId}</TableCell>
                        <TableCell>{item.serviceCode}</TableCell>
                        <TableCell sx={{ borderLeft: '8px solid', borderLeftColor: 'neutral100' }}>
                            <DisplayShortWithTooltip
                                maxLength={10}
                                text={item.institution ?? ''}
                            />
                        </TableCell>
                        <TableCell>
                            <DisplayShortWithTooltip
                                maxLength={8}
                                text={item.group ?? ''}
                            />
                        </TableCell>
                        <TableCell>{item.codeActiviteit}</TableCell>
                        <TableCell>{item.relationNumber ?? ''}</TableCell>
                        <TableCell>
                            {item.amount !== undefined && (
                                <DisplayAmountWithVatRate
                                    amountWithVatRate={{ amount: item.amount, vatRate: item.vatRate }}
                                    display="short"
                                />
                            )}
                        </TableCell>
                        <TableCell>{item.isCounterBooked ? 'TB' : ''}</TableCell>
                        <TableCell>
                            {notFundedReasonText && (
                                <DisplayShortWithTooltip
                                    maxLength={15}
                                    text={notFundedReasonText}
                                />
                            )}
                        </TableCell>
                        <TableCell>
                            {item.fundingError && (
                                <DisplayShortWithTooltip
                                    maxLength={12}
                                    text={language.fundingErrorCode[item.fundingError]}
                                />
                            )}
                        </TableCell>
                        <TableCell>
                            {isUsedForReprocessing && item.fundingError && (
                                <Dropdown
                                    onChange={(value) => {
                                        onChangeFundingErrorInformation(value as FundingErrorInformation, index);
                                    }}
                                    selectOptions={getFundingErrorInformationOptions(item.fundingErrorInformation)}
                                    value={item.fundingErrorInformation}
                                />
                            )}
                        </TableCell>
                        <TableCell>{item.runNumber}</TableCell>
                        <TableCell>
                            {isUsedForReprocessing && item.reprocessPractitioner && (
                                <DisplayShortWithTooltip
                                    maxLength={9}
                                    practitioner={item.reprocessPractitioner}
                                />
                            )}
                        </TableCell>
                        <TableCell>
                            {isUsedForReprocessing &&
                                item.reprocessDate &&
                                DateTime.from(item.reprocessDate).format('DD-MM-YYYY HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                            {isProcessingLabelVisible(item, isUsedForReprocessing, reprocessedActivities)
                                ? language.finance.processing
                                : ''}
                        </TableCell>
                    </TableRow>
                );
            })}
        </TableBody>
    );

    function mapFinanceRow(item: Financing): FinancingRow {
        const result: FinancingRow = { ...item };

        if (item.invoice?.id) {
            result.group = item.invoice?.requesterGroup;
            result.relationNumber = item.invoice?.requesterGroup
                ? item.invoice.requesterGroupRelationNumber
                : item.invoice.providerOrganizationRelationNumber;
            result.amount = item.invoice.amount;
            result.institution = item.invoice?.requesterGroup ? null : item.invoice.providerOrganization;
        }

        if (item.declaration?.id) {
            result.codeActiviteit = item.declaration.codeActiviteit;
        }

        if (item.patientInvoice?.id) {
            result.codeActiviteit = item.patientInvoice.codeActiviteit;
            result.amount = item.patientInvoice.amount;
        }

        if (item.singularPatientInvoice?.id) {
            result.amount = item.singularPatientInvoice.amount;
            result.vatRate = item.singularPatientInvoice.vatRate;
        }

        result.isCounterBooked =
            Boolean(item.invoice?.counterBookingId) ||
            Boolean(item.declaration?.counterBookingId) ||
            item.patientInvoice?.isCounterBooked ||
            Boolean(item.singularPatientInvoice?.counterBookingId);
        result.runNumber =
            item.invoice?.runNumber ||
            item.declaration?.runNumber ||
            item.patientInvoice?.runNumber ||
            item.singularPatientInvoice?.runNumber;

        return result;
    }

    function isProcessingLabelVisible(item: Financing, isUsedForReprocessing: boolean, activities: string[]) {
        return isUsedForReprocessing && activities.includes(item.id);
    }

    function getNotFundedReasonText(item: FinancingRow) {
        if (item.notFundedReason) {
            return language.notFundedReason[item.notFundedReason];
        } else if (item?.patientInvoice.endDate) {
            const subTrack = {
                ...item.patientInvoice,
                endDate: item.patientInvoice.endDate,
            };

            return canSubTrackBeExported(subTrack) && subTrack.gateSpecialistAgbCode === null
                ? language.fundingErrorCode.missingPoortfunctionaris
                : null;
        }

        return null;
    }
}
